/* Solo Wellness · motion.js
   One small, dependency-free motion layer. Transform and opacity only.
   - [data-split]      headings rise word by word when revealed
   - [data-parallax]   element drifts at a speed while in view (e.g. data-parallax="0.12")
   - [data-progress]   gets --enter (0→1 while entering) and --p (0→1 across the full pass)
   - [data-fill]       words light up with scroll progress (quote)
   - [data-count-to]   numbers count up on reveal (keeps prefix/suffix, e.g. "+7%", "17/20")
   - [data-tilt]       pointer tilt on fine pointers
   - .btn--magnetic    button leans toward the cursor
   Everything switches off under prefers-reduced-motion. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const root = document.documentElement;
  if (reduce) root.classList.add('motion-off');

  /* ---------- Split headings into words ---------- */
  const split = (el) => {
    if (el.dataset.splitDone || reduce) return;
    // only split plain-text headings, leave rich markup untouched
    if (Array.from(el.childNodes).some((n) => n.nodeType === 1 && n.tagName !== 'BR')) return;
    let i = 0;
    const frag = document.createDocumentFragment();
    Array.from(el.childNodes).forEach((n) => {
      if (n.nodeType === 1) { frag.appendChild(n.cloneNode()); return; }
      n.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
        const w = document.createElement('span');
        w.className = 'w';
        const inner = document.createElement('span');
        inner.textContent = part;
        inner.style.setProperty('--i', i++);
        w.appendChild(inner);
        frag.appendChild(w);
      });
    });
    el.textContent = '';
    el.appendChild(frag);
    el.style.setProperty('--words', i);
    el.dataset.splitDone = '1';
  };

  /* ---------- Fill-on-scroll words (quote) ---------- */
  const prepFill = (el) => {
    if (el.dataset.fillDone || reduce) return;
    const text = el.textContent.trim().replace(/\s+/g, ' ');
    const words = text.split(' ');
    el.innerHTML = '';
    words.forEach((word, i) => {
      const s = document.createElement('span');
      s.className = 'fw';
      s.textContent = word;
      s.style.setProperty('--i', i);
      el.appendChild(s);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
    el.style.setProperty('--n', words.length);
    el.dataset.fillDone = '1';
  };

  /* ---------- Count up ---------- */
  const countUp = (el) => {
    if (el.dataset.counted) return;
    el.dataset.counted = '1';
    const raw = (el.dataset.countTo || el.textContent).trim();
    const m = raw.match(/^(\D*?)(\d+(?:[.,]\d+)?)(.*)$/);
    if (!m || reduce) return;
    const [, pre, num, post] = m;
    const target = parseFloat(num.replace(',', '.'));
    const dec = (num.split(/[.,]/)[1] || '').length;
    const dur = 1600;
    const t0 = performance.now();
    el.style.minWidth = `${el.offsetWidth}px`;
    const tick = (t) => {
      const k = clamp((t - t0) / dur);
      const e = 1 - Math.pow(1 - k, 4);
      el.textContent = `${pre}${(target * e).toFixed(dec)}${post}`;
      if (k < 1) requestAnimationFrame(tick); else { el.textContent = raw; el.style.minWidth = ''; }
    };
    requestAnimationFrame(tick);
  };

  /* ---------- Reveal hooks: split + count when an element comes into view ---------- */
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const el = e.target;
      el.classList.add('is-in');
      $$('[data-count-to]', el).forEach(countUp);
      if (el.matches('[data-count-to]')) countUp(el);
      io.unobserve(el);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

  /* ---------- Scroll-linked engine (one rAF, only elements in view) ---------- */
  const live = new Set();
  const vis = new IntersectionObserver((entries) => {
    entries.forEach((e) => (e.isIntersecting ? live.add(e.target) : live.delete(e.target)));
    kick();
  }, { rootMargin: '20% 0px 20% 0px' });

  let ticking = false;
  const frame = () => {
    ticking = false;
    const vh = window.innerHeight;
    // read every rect first, then write, so the loop never forces layout per element
    const reads = [];
    live.forEach((el) => reads.push([el, (el._par !== undefined && el.parentElement ? el.parentElement : el).getBoundingClientRect()]));
    reads.forEach(([el, r]) => {
      if (el._par !== undefined) {
        const center = r.top + r.height / 2 - vh / 2;
        el.style.transform = `translate3d(0, ${(center * -el._par).toFixed(1)}px, 0) scale(${el._parScale})`;
      }
      if (el._prog) {
        const enter = clamp((vh - r.top) / (vh * 0.75));
        const p = clamp((vh - r.top) / (vh + r.height));
        el.style.setProperty('--enter', enter.toFixed(3));
        el.style.setProperty('--p', p.toFixed(3));
        el.style.setProperty('--out', clamp(-r.top / Math.max(r.height, 1)).toFixed(3));
      }
    });
  };
  const kick = () => { if (!ticking && live.size) { ticking = true; requestAnimationFrame(frame); } };

  /* ---------- Pointer effects ---------- */
  const tilt = (el) => {
    if (!fine || reduce || el._tilt) return;
    el._tilt = true;
    const max = parseFloat(el.dataset.tilt) || 6;
    let raf = 0;
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--rx', `${(-y * max).toFixed(2)}deg`);
        el.style.setProperty('--ry', `${(x * max).toFixed(2)}deg`);
        el.style.setProperty('--mx', `${((x + 0.5) * 100).toFixed(1)}%`);
        el.style.setProperty('--my', `${((y + 0.5) * 100).toFixed(1)}%`);
      });
    });
    el.addEventListener('pointerleave', () => { el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); });
  };
  const magnetic = (el) => {
    if (!fine || reduce || el._mag) return;
    el._mag = true;
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--tx', `${((e.clientX - r.left - r.width / 2) * 0.18).toFixed(1)}px`);
      el.style.setProperty('--ty', `${((e.clientY - r.top - r.height / 2) * 0.28).toFixed(1)}px`);
    });
    el.addEventListener('pointerleave', () => { el.style.setProperty('--tx', '0px'); el.style.setProperty('--ty', '0px'); });
  };

  /* ---------- Init (also on theme editor section reloads) ---------- */
  const init = (scope = document) => {
    $$('[data-split]', scope).forEach((el) => { split(el); io.observe(el); });
    $$('[data-fill]', scope).forEach(prepFill);
    $$('[data-count-to]', scope).forEach((el) => io.observe(el));
    $$('[data-observe]', scope).forEach((el) => io.observe(el));
    $$('[data-tilt]', scope).forEach(tilt);
    $$('.btn--magnetic', scope).forEach(magnetic);
    if (reduce) {
      $$('[data-progress]', scope).forEach((el) => { el.style.setProperty('--enter', 1); el.style.setProperty('--p', 1); el.style.setProperty('--out', 0); });
      return;
    }
    // containers can hand parallax to their image (image_tag can't take data attributes)
    $$('[data-parallax-child]', scope).forEach((box) => { const img = box.querySelector('img'); if (img) img.dataset.parallax = box.dataset.parallaxChild; });
    $$('[data-parallax]', scope).forEach((el) => {
      const speed = parseFloat(el.dataset.parallax) || 0.1;
      // small screens get a gentler drift
      el._par = window.innerWidth < 750 ? speed * 0.6 : speed;
      el._parScale = parseFloat(el.dataset.parallaxScale || (el.tagName === 'IMG' ? 1.14 : 1));
      vis.observe(el);
    });
    $$('[data-progress]', scope).forEach((el) => { el._prog = true; vis.observe(el); });
    kick();
  };

  window.addEventListener('scroll', kick, { passive: true });
  window.addEventListener('resize', kick, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* ---------- Steps that light up as you pass them ---------- */
  const lit = new IntersectionObserver((entries) => {
    entries.forEach((e) => e.target.classList.toggle('is-lit', e.isIntersecting || e.boundingClientRect.top < 0));
  }, { rootMargin: '0px 0px -45% 0px' });
  const initLit = (scope = document) => $$('[data-lit]', scope).forEach((el) => lit.observe(el));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initLit()); else initLit();

  /* ---------- Cart bump when something lands in the bag ---------- */
  document.addEventListener('solo:solo_add_to_cart', () => {
    $$('.header-icon[data-drawer-open="CartDrawer"]').forEach((b) => { b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump'); });
  });

  /* ---------- Announcement rotator (pause button, hover/focus pause, editor-safe) ---------- */
  const initRotator = (scope = document) => {
    const rot = scope.querySelector('[data-rotator]');
    if (!rot || rot._rot) return;
    rot._rot = true;
    const items = $$('[data-rotator-item]', rot);
    const toggle = rot.querySelector('[data-rotator-toggle]');
    let idx = 0, hover = false, stopped = false;
    rot.addEventListener('pointerenter', () => { hover = true; });
    rot.addEventListener('pointerleave', () => { hover = false; });
    rot.addEventListener('focusin', (e) => { if (e.target !== toggle) hover = true; });
    rot.addEventListener('focusout', () => { hover = false; });
    if (toggle) toggle.addEventListener('click', () => { stopped = !stopped; toggle.setAttribute('aria-pressed', stopped ? 'true' : 'false'); });
    if (items.length < 2) { if (toggle) toggle.hidden = true; return; }
    const timer = setInterval(() => {
      if (!rot.isConnected) { clearInterval(timer); return; }
      if (hover || stopped || document.hidden) return;
      items[idx].classList.remove('is-active');
      items[idx].inert = true;
      idx = (idx + 1) % items.length;
      items[idx].classList.add('is-active');
      items[idx].inert = false;
    }, 4500);
  };
  initRotator();
  document.addEventListener('shopify:section:load', (e) => initRotator(e.target));

  /* ---------- Marquee pause toggle ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-marquee-toggle]');
    if (!b) return;
    const m = b.closest('.marquee');
    const on = !m.classList.contains('is-paused');
    m.classList.toggle('is-paused', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });

  /* ---------- Back to top ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-back-to-top]');
    if (!b) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    document.getElementById('MainContent')?.focus({ preventScroll: true });
  });
})();
